import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import {
  fetchFullReport,
  fetchMyProfile,
  type FullReportResponse,
} from '../../lib/apiClient';
import { routes } from '../../lib/assessment/routes';
import { hasWorkbooks } from '../../lib/report/reportView';
import { PRINT_PAGE_CSS, printReport } from '../../lib/report/printCss';
import { BottleneckReportPrint } from './BottleneckReportPrint';
import { PerspectiveReportPrint, WorkFitReportPrint } from './TypedReportPrint';
import { PrintBar, PrintFooter } from './printPages';
import { usePageMeta } from '../../lib/usePageMeta';

/** Production's print titles, one per assessment type. */
const PRINT_META: Record<string, string> = {
  bottleneck: 'Full Report | High Performer OS',
  work_fit: 'Work Fit Report | High Performer OS',
  perspective: 'Perspective Report | High Performer OS',
};

const PRINT_META_PENDING = 'Your Report | High Performer OS';

/**
 * Production's per-document states.
 *
 * Each print page words its own loading line, and its Work Fit and Perspective
 * pages word the locked refusal differently from each other — "once unlocked"
 * against "once the payment is confirmed". V2 printed one generic sentence for
 * all three.
 *
 * The Bottleneck print page has no such line: production heads its refusal
 * "Report Not Available" instead, which is why `loading` is optional here.
 */
const PRINT_STATES: Record<string, { loading?: string; locked: string }> = {
  bottleneck: {
    locked:
      'This report requires a completed purchase. Return to the dashboard and your report will be available once unlocked.',
  },
  work_fit: {
    loading: 'Preparing your Work Fit Report...',
    locked:
      'This report requires a completed purchase. Return to the dashboard and your report will be available once unlocked.',
  },
  perspective: {
    loading: 'Preparing your Perspective Report...',
    locked:
      'This report requires a completed purchase. Return to the dashboard and your report will be available once the payment is confirmed.',
  },
};

type PageState =
  | { phase: 'loading' }
  | { phase: 'locked'; assessmentType: string }
  | { phase: 'not-found' }
  | { phase: 'error' }
  | { phase: 'ok'; data: FullReportResponse };

/**
 * Print-optimized full report. Same gated payload as the on-screen report, rendered
 * with print page CSS and an explicit Print button (the browser's print-to-PDF is
 * the PDF path). Auto-opens the print dialog once the report has loaded.
 */
export function ReportPrint({ id }: { id: string }) {
  const { status, session } = useAuth();
  // Derived from `status` so the fetch gate matches the render gate below; a
  // password-recovery session carries a valid token but is not signed in.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const printed = useRef(false);
  /** Production prints "Prepared for {name}" on the cover. */
  const [preparedFor, setPreparedFor] = useState<string | null>(null);
  /**
   * The assessment type, once anything has told us. Before the payload arrives
   * there is nothing to key the wording off, so the states fall back to the
   * neutral sentence rather than guessing at a type.
   */
  const lockedType =
    state.phase === 'ok'
      ? state.data.report.assessmentType
      : state.phase === 'locked'
        ? state.assessmentType
        : '';

  usePageMeta({
    title:
      state.phase === 'ok'
        ? (PRINT_META[state.data.report.assessmentType] ?? PRINT_META_PENDING)
        : PRINT_META_PENDING,
    noindex: true,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      // A failed name lookup is not a failed document: the line is omitted.
      const res = await fetchMyProfile(token).catch(() => null);
      if (cancelled) return;
      const name =
        res?.state === 'ok'
          ? [res.profile.first_name, res.profile.last_name].filter(Boolean).join(' ').trim()
          : '';
      setPreparedFor(name || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (status === 'loading' || !token) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await fetchFullReport(token, id);
      if (cancelled) return;
      if (res.state === 'ok') setState({ phase: 'ok', data: res.data });
      else if (res.state === 'not-found') setState({ phase: 'not-found' });
      else if (res.state === 'locked')
        setState({ phase: 'locked', assessmentType: res.assessmentType ?? '' });
      else setState({ phase: 'error' });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, token, status]);

  useEffect(() => {
    if (state.phase === 'ok' && !printed.current) {
      printed.current = true;
      // Defer to let the DOM paint before opening the print dialog.
      const t = setTimeout(() => printReport(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state.phase]);

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">Sign in to print your report.</p>
        <AuthStatus />
      </section>
    );
  }
  if (state.phase === 'loading')
    return (
      <p className="text-muted-foreground" data-testid="print-loading">
        {PRINT_STATES[lockedType]?.loading ?? 'Preparing your report…'}
      </p>
    );
  // Production's wording for a report the caller may not read.
  if (state.phase === 'not-found')
    return (
      <p className="text-foreground" data-testid="print-not-found">
        You do not have access to this report.
      </p>
    );
  if (state.phase === 'error')
    return <p className="text-destructive">Your report could not be loaded.</p>;
  if (state.phase === 'locked') {
    return (
      <section className="flex flex-col gap-3" data-testid="print-locked">
        <p className="text-foreground">
          {PRINT_STATES[lockedType]?.locked ??
            'This report requires a completed purchase. Return to the dashboard and your report will be available once unlocked.'}
        </p>
        <Link
          href={routes.report(id)}
          className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Go to the report
        </Link>
      </section>
    );
  }

  const { result, report } = state.data;
  // The shared rule, not a second copy: a workbook link that 404s for someone
  // who has paid is exactly what re-deriving it produces.
  const showWorkbooks = hasWorkbooks(report.assessmentType);

  const document =
    report.assessmentType === 'bottleneck' ? (
      <BottleneckReportPrint
        result={result}
        content={report.content}
        preparedFor={preparedFor}
        previousResult={state.data.previousResult ?? null}
      />
    ) : report.assessmentType === 'work_fit' ? (
      <WorkFitReportPrint
        result={result}
        content={report.content}
        environmentNeeds={report.environmentNeeds}
        misfitSignals={report.misfitSignals}
        preparedFor={preparedFor}
      />
    ) : (
      <PerspectiveReportPrint
        result={result}
        content={report.content}
        interpretationTraps={report.interpretationTraps}
        preparedFor={preparedFor}
      />
    );

  return (
    <div className="flex flex-col gap-6">
      <style>{PRINT_PAGE_CSS}</style>

      {/* Production's print bar: back, the workbooks, and Save as PDF. */}
      <PrintBar>
        <Link
          href={routes.report(id)}
          data-testid="print-back"
          className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          ← Back
        </Link>
        {showWorkbooks ? (
          <>
            <Link
              href={routes.reportWorkbook(id, '7-day')}
              data-testid="print-workbook-7-day"
              className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              7-Day Journal
            </Link>
            <Link
              href={routes.reportWorkbook(id, '30-day')}
              data-testid="print-workbook-30-day"
              className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              30-Day Workbook
            </Link>
          </>
        ) : null}
        <button
          type="button"
          onClick={() => printReport()}
          data-testid="print-save"
          className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          ⬇ Save as PDF
        </button>
      </PrintBar>

      {document}

      <PrintFooter />
    </div>
  );
}
